import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { IonSkeletonText, IonIcon } from "@ionic/react";
import { cartOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ImageType, Product, SharedCart } from "@core/types";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { BackButton } from "../../../parhato2/src/entities";
import { useGetAllSharedCartsInfiniteQuery } from "../../../providers/cart";
import { useGetIncludeProductsQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useAuth } from "../../../contexts/auth-context";
import { Image } from "../../../components/Image/Image";
import { getPreviewImage } from "../../../utils/getPreviewImage";
import { placeholderLogo } from "../../../parhato2/src/shared/assets/images/images";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";

const PREVIEW_IMAGES_PER_CART = 4;

const SkeletonCard = () => (
  <div className="flex w-full rounded-xl p-2 bg-[#f4f4f4]">
    <div className="flex-shrink-0 grid grid-cols-2 w-[6.25rem] h-[6.25rem] lg:h-[10rem] lg:w-[10rem] rounded-lg overflow-hidden">
      {[...Array(4)].map((_, i) => (
        <IonSkeletonText key={i} animated className="w-full h-full m-0" />
      ))}
    </div>
    <div className="flex-1 mx-2 flex flex-col gap-1.5 justify-center">
      <IonSkeletonText animated className="h-4 w-36 m-0 rounded-md" />
      <IonSkeletonText animated className="h-3 w-20 m-0 rounded-md" />
    </div>
    <div className="flex flex-col items-end gap-1 mx-1">
      <IonSkeletonText animated className="h-3 w-16 m-0 rounded-md" />
    </div>
  </div>
);

type CartCardProps = {
  cart: SharedCart | null;
  productMap: Map<string, Product>;
  onClick: () => void;
};

const CartCard = ({ cart, productMap, onClick }: CartCardProps) => {
  const { t } = useTranslation();

  const getProductWord = (n: number) => {
    const d = n % 10;
    const dd = n % 100;
    if (dd >= 11 && dd <= 14) return t("carts_products_plural");
    if (d === 1) return t("carts_product_singular");
    if (d >= 2 && d <= 4) return t("carts_products_few");
    return t("carts_products_plural");
  };

  const previewIds = cart?.products.slice(0, PREVIEW_IMAGES_PER_CART).map((p) => String(p.id)) ?? [];
  const previewProducts = previewIds.map((id) => productMap.get(id)).filter(Boolean) as Product[];
  const placeholdersCount = PREVIEW_IMAGES_PER_CART - previewProducts.length;

  const productCount = cart?.products.length ?? 0;
  const dateStr = dayjs(cart?.created_at ?? "").format("DD.MM.YYYY");

  const allImages = [
    ...previewProducts.map((p) => ({ key: String(p.id), src: getPreviewImage(p.images?.[0] as ImageType) })),
    ...Array.from({ length: placeholdersCount }, (_, i) => ({ key: `ph-${i}`, src: undefined })),
  ];

  return (
    <div className="flex w-full lg:h-[11rem] rounded-xl p-2 bg-[#f4f4f4]" onClick={onClick}>
      <div className="flex-shrink-0 grid grid-cols-2 w-[6.25rem] h-[6.25rem] lg:h-[10rem] lg:w-[10rem] rounded-lg overflow-hidden">
        {allImages.map(({ key, src }) => (
          <div key={key} className="flex items-center justify-center w-full h-full rounded-2xl overflow-hidden">
            <Image image={src} className="w-full h-full m-0.5" placeholder={placeholderLogo} />
          </div>
        ))}
      </div>

      <div className="flex-1 mx-2">
        <span className="font-bold text-black">{cart?.name}</span>
        <p className="text-gray-400 text-sm font-bold">
          {productCount} {getProductWord(productCount)}
        </p>
        {cart?.description ? <p className="text-gray-500 text-xs mt-1 line-clamp-2">{cart.description}</p> : null}
      </div>

      <div className="flex flex-col items-end mx-1 gap-1">
        <div className="text-gray-400 text-xs font-semibold">{dateStr}</div>
      </div>
    </div>
  );
};

const MySharedCartsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { isUserAuthenticated } = useAuth();
  const { activeShippingZone } = useActiveOrMainShippingZone();

  const {
    data: sharedCartsPages,
    isLoading: isLoadingCarts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetAllSharedCartsInfiniteQuery({
    options: { enabled: !!isUserAuthenticated },
  });

  const sharedCarts = useMemo(() => sharedCartsPages?.pages.flat() ?? [], [sharedCartsPages]);

  const allProductIds = useMemo(() => {
    if (sharedCarts.length === 0) return [];
    const ids = new Set<string>();
    sharedCarts.forEach((cart) =>
      cart?.products.slice(0, PREVIEW_IMAGES_PER_CART).forEach((p) => ids.add(String(p.id))),
    );
    return Array.from(ids);
  }, [sharedCarts]);

  const { data: products, isLoading: isLoadingProducts } = useGetIncludeProductsQuery({
    include: allProductIds,
    params: { zoneId: activeShippingZone.id },
    options: { enabled: allProductIds.length > 0 },
  });

  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    (products ?? []).forEach((p) => map.set(String(p.id), p));
    return map;
  }, [products]);

  const isLoading = isLoadingCarts || (allProductIds.length > 0 && isLoadingProducts);

  const handleCartClick = (id: string | undefined) => {
    if (!id) return;
    history.push(`/shared-cart/${id}`);
  };

  return (
    <Page>
      <Page.Header className="flex items-center justify-start h-16 mt-[env(safe-area-inset-top)]">
        <MaxWidthContainer classList="flex items-center bg-white w-full">
          <div className="flex justify-center items-center w-full relative">
            <div className="absolute left-2.5">
              <BackButton />
            </div>
            <div className="flex-1 text-center text-[1.25rem] font-bold">{t("Saved carts")}</div>
          </div>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content scrollEvents className="pb-28">
        <PullToRefresh/>
        <MaxWidthContainer classList="flex flex-col gap-3 p-3 pt-4">
          {isLoading && (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          )}

          {!isLoading && (!sharedCarts || sharedCarts.length === 0) && (
            <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
              <IonIcon icon={cartOutline} className="text-[3.5rem] text-gray-300" />
              <div className="text-gray-500 text-sm">{t("Place for your saved carts")}</div>
            </div>
          )}

          {!isLoading &&
            sharedCarts.map((cart) => (
              <CartCard key={cart?.id} cart={cart} productMap={productMap} onClick={() => handleCartClick(cart?.id)} />
            ))}

          {hasNextPage && (
            <button
              type="button"
              className="w-full py-3 rounded-xl bg-[#f4f4f4] text-sm font-medium text-gray-600 disabled:opacity-50"
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
            >
              {isFetchingNextPage ? "Загрузка…" : "Загрузить ещё"}
            </button>
          )}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default MySharedCartsPage;
